import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import "./Pricing.css";
import PricingCalculator from "./PricingCalculator";

const plans = [
  {
    label: "KISAN",
    name: "Free",
    unit: "1 farm · up to 2 hectares · 1 month",
    description:
      "Start with one farm for one month and access the core Farmology features at no cost.",
    features: [
      "All core Farmology features",
      "1 farm active for 1 month",
      "Farm setup up to 2 hectares",
      "30 Crop Clinic disease detection units",
      "Farm report on the 5th day",
      "Report delivered by email",
      "Access reports from your Farmology panel",
    ],
    action: "Start free",
    featured: false,
    link: "/user/trial-add-farm",
  },
  {
    label: "GROWER",
    name: "₹200",
    unit: "per hectare / month",
    description:
      "Keep your farm active with Farmology intelligence on a simple per-hectare monthly plan.",
    features: [
      "All core Farmology features",
      "Farm stays active while subscribed",
      "₹200 per hectare / month",
      "Crop Clinic disease detection",
      "Farm reports and insights",
      "Reports delivered by email",
      "Access reports from your Farmology panel",
    ],
    action: "Get Started",
    featured: true,
    link: "/user/login?redirect=/user/add-farm-now",
  },
  {
    label: "ENTERPRISE",
    name: "Custom",
    unit: "Custom plan & setup",
    description:
      "A tailored Farmology setup for FPOs, co-ops, agri-businesses and larger operations.",
    features: [
      "All core Farmology features",
      "Custom plan based on your requirements",
      "Custom farm and account setup",
      "Flexible farm coverage",
      "Custom Crop Clinic requirements",
      "Custom reporting and workflows",
      "Dedicated support and onboarding",
    ],
    action: "Talk to us",
    featured: false,
    link: "/contact",
  },
];

const faqs = [
  {
    question: "What do I get with the Free plan?",
    answer:
      "The Free plan is active for one month and lets you set up one farm up to 2 hectares. It includes the same core Farmology features, 30 Crop Clinic disease detection units, a farm report on the 5th day, email report delivery and report access from your Farmology panel.",
  },
  {
    question: "How does the Grower plan work?",
    answer:
      "The Grower plan costs ₹200 per hectare per month. Your farm stays active while the plan is active, and you continue using the Farmology features, Crop Clinic and farm reports.",
  },
  {
    question: "Can I get a custom setup for my organisation?",
    answer:
      "Yes. Enterprise customers can work with the Farmology team to create a custom plan and setup based on their farm coverage, reporting, workflows and other operational requirements.",
  },
  {
    question: "How will I receive my farm report?",
    answer:
      "Farm reports can be delivered to your email and can also be accessed directly from your Farmology panel.",
  },
];

function Pricing() {
  const [openFaq, setOpenFaq] = useState(null);
  const [cookies] = useCookies(["user_auth_token"]);
  const navigate = useNavigate();

  // Start Free flow:
  // - Logged in -> directly open the trial farm page.
  // - Not logged in -> open login and preserve the trial destination.
  const handleStartFree = () => {
    const destination = "/user/trial-add-farm";

    // Remember that the user started the Free Trial flow
    sessionStorage.setItem("auth_redirect", destination);

    if (cookies.user_auth_token) {
      navigate(destination);
      return;
    }

    navigate(`/user/login?redirect=${encodeURIComponent(destination)}`);
  };

  const handlePaidStart = () => {
    const destination = "/user/add-farm-now";

    // Remember that the user started the paid farm flow
    sessionStorage.setItem("auth_redirect", destination);

    if (cookies.user_auth_token) {
      navigate(destination);
      return;
    }

    navigate(`/user/login?redirect=${encodeURIComponent(destination)}`);
  };

  return (
    <main className="pricing-page">
      {/* =========================================================
          HERO
      ========================================================== */}
      <section className="pricing-hero">
        <div className="pricing-hero-overlay"></div>

        <div className="pricing-container pricing-hero-content">
          <div className="pricing-hero-eyebrow">
            <span></span>
            FARMOLOGY PRICING
          </div>

          <h1>
            Plans built
            <br />
            <span>for every farm.</span>
          </h1>

          <p className="pricing-hero-description">
            Simple, farm-first plans that give you the intelligence you need to
            make better decisions across every hectare.
          </p>

          <div className="pricing-breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>Pricing</span>
          </div>
        </div>

        <div className="pricing-hero-number">03</div>

        <div className="pricing-hero-bottom">
          <span>FARMOLOGY</span>
          <div></div>
          <span>SMART AGRICULTURE</span>
        </div>
      </section>

      {/* =========================================================
          PRICING PLANS
      ========================================================== */}
      <section className="pricing-section pricing-intro">
        <div className="pricing-container">
          <div className="pricing-section-heading pricing-centered">
            <span className="pricing-eyebrow">PRICING</span>

            <h2>Simple plans for every farm</h2>

            <p>
              Start free for one month, keep your farm active with the Grower
              plan, or work with us to create a custom setup for your operation.
            </p>
          </div>

          <div className="pricing-plans-grid">
            {plans.map((plan) => (
              <article
                className={`pricing-plan-card ${
                  plan.featured ? "pricing-plan-card-featured" : ""
                }`}
                key={plan.label}
              >
                {plan.featured && (
                  <span className="pricing-plan-badge">MOST CHOSEN</span>
                )}

                <div className="pricing-plan-label">{plan.label}</div>

                <h3>{plan.name}</h3>

                <div className="pricing-plan-unit">{plan.unit}</div>

                <p className="pricing-plan-description">{plan.description}</p>

                <ul className="pricing-plan-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <span>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                {plan.label === "KISAN" ? (
                  <button
                    type="button"
                    onClick={handleStartFree}
                    className="pricing-plan-button"
                  >
                    {plan.action}
                  </button>
                ) : plan.label === "GROWER" ? (
                  <button
                    type="button"
                    onClick={handlePaidStart}
                    className="pricing-plan-button pricing-plan-button-primary"
                  >
                    {plan.action}
                  </button>
                ) : (
                  <Link to={plan.link} className="pricing-plan-button">
                    {plan.action}
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          PRICING CALCULATOR
      ========================================================== */}
      <section className="pricing-section pricing-calculator-section">
        <div className="pricing-container">
          <div className="pricing-section-heading pricing-centered">
            <span className="pricing-eyebrow">CALCULATE YOUR PRICE</span>

            <h2>Calculate your Grower plan</h2>

            <p>
              Select your crop, enter your farm size and see the estimated
              monthly cost at ₹200 per hectare.
            </p>
          </div>

          <div className="pricing-calculator-wrapper">
            <PricingCalculator />
          </div>
        </div>
      </section>

      {/* =========================================================
          COMPARISON
      ========================================================== */}

      {/* =========================================================
          BILLING FAQ
      ========================================================== */}
      <section className="pricing-section pricing-faq">
        <div className="pricing-container">
          <div className="pricing-faq-layout">
            <div className="pricing-faq-intro">
              <span className="pricing-eyebrow">BILLING FAQ</span>

              <h2>Before you subscribe</h2>

              <p>
                Have a question about plans, pricing or getting started?
                We&apos;re here to help.
              </p>

              <div className="pricing-faq-help">
                <div className="pricing-faq-help-icon">?</div>

                <div>
                  <strong>Need help choosing?</strong>

                  <p>Talk to the Farmology team about your farm.</p>

                  <Link to="/contact">Contact us ↗</Link>
                </div>
              </div>
            </div>

            <div className="pricing-faq-list">
              {faqs.map((faq, index) => {
                const open = openFaq === index;

                return (
                  <div
                    className={`pricing-faq-item ${open ? "open" : ""}`}
                    key={faq.question}
                  >
                    <button
                      type="button"
                      className="pricing-faq-question"
                      onClick={() => setOpenFaq(open ? null : index)}
                      aria-expanded={open}
                    >
                      <span className="pricing-faq-question-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <span className="pricing-faq-question-text">
                        {faq.question}
                      </span>

                      <span className="pricing-faq-toggle">
                        {open ? "−" : "+"}
                      </span>
                    </button>

                    <div className="pricing-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          CTA
      ========================================================== */}
      <section className="pricing-cta-section">
        <div className="pricing-container">
          <div className="pricing-cta-box">
            <div>
              <div className="pricing-cta-eyebrow">CUSTOM FARMOLOGY SETUP</div>

              <h2>Build a plan around your operation.</h2>

              <p>
                For FPOs, co-ops, agri-businesses and larger holdings, we can
                create a custom plan and setup around your specific
                requirements.
              </p>
            </div>

            <div className="pricing-cta-actions">
              <Link to="/contact" className="pricing-btn pricing-btn-white">
                Talk to us
                <span>↗</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Pricing;
